import {
  Alignment,
  Button,
  ButtonGroup,
  Callout,
  Checkbox,
  Icon,
  InputGroup,
  Intent,
  Popover,
  PopoverInteractionKind,
  Position
} from '@blueprintjs/core'
import cx from 'classnames'
import { BotConfig, BotDetails } from 'enablex/sdk'
import { confirmDialog, lang, telemetry, toast, utils } from 'enablex/shared'
import _ from 'lodash'
import React, { Component, Fragment, useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { generatePath, RouteComponentProps } from 'react-router'

import api from '~/app/api'
import { Downloader } from '~/app/common/Downloader'
import LoadingSection from '~/app/common/LoadingSection'
import PageContainer from '~/app/common/PageContainer'
import SplitPage from '~/app/common/SplitPage'
import { AppState } from '~/app/rootReducer'
import AccessControl from '~/auth/AccessControl'
import { getActiveWorkspace } from '~/auth/basicAuth'
import { fetchModules } from '~/management/modules/reducer'
import { fetchBotHealth, fetchBots, fetchBotNLULanguages, fetchBotForAgent } from '~/workspace/bots/reducer'
import { filterList } from '~/workspace/util'

import BotItemCompact from './BotItemCompact'
import BotItemPipeline from './BotItemPipeline'
import DeployModal from './DeployModal'
import EditStageModal from './EditStageModal'
import ImportBotModal from './ImportBotModal'
import RollbackBotModal from './RollbackBotModal'
import style from './style.scss'
import BotIcon from '~/icons/BotIcon'

const botFilterFields = ['name', 'id', 'description']

type Props = ConnectedProps<typeof connector> & RouteComponentProps

class Bots extends Component<Props> {
  state = {
    isCreateBotModalOpen: false,
    isRollbackModalOpen: false,
    isDeploymentModalOpen: false,
    isImportBotModalOpen: false,
    isEditStageModalOpen: false,
    focusedBot: null,
    whiteListedDomains: [],
    details: {},
    name: '',
    selectedStage: null,
    archiveUrl: undefined,
    archiveName: '',
    filter: '',
    showFilters: false,
    needApprovalFilter: false,
    isLaptopScreen: false,
    windowDimensions: {
      width: window.innerWidth,
      height: window.innerHeight
    }
  }

  componentDidMount() {
    this.props.fetchBots()
    if (this.props.profile?.isAgentUser || this.props.profile?.isSupervisor) {
      this.props.fetchBotForAgent(this.props.profile.email)
    }
    this.props.fetchBotHealth()
    this.props.fetchBotNLULanguages()

    if (!this.props.loadedModules.length && this.props.profile && this.props.profile.isSuperAdmin) {
      this.props.fetchModules()
    }
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    //telemetry.startFallback(api.getSecured({ useV1: true })).catch()
  }
  handleResize = () => {
    this.setState({
      windowDimensions: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    })
    this.setState({ isLaptopScreen: this.state.windowDimensions.width <= 1344 ? true : false })
    // console.log(this.state.windowDimensions.width, this.state.isLaptopScreen)
  }

  toggleCreateBotModal = () => {
    this.setState({ isCreateBotModalOpen: !this.state.isCreateBotModalOpen })
  }

  toggleImportBotModal = () => {
    this.setState({ isImportBotModalOpen: !this.state.isImportBotModalOpen })
  }

  async exportBot(botId: string) {
    this.setState({
      archiveUrl: `/admin/workspace/bots/${botId}/export`,
      archiveName: `bot_${botId}_${Date.now()}.tgz`
    })
  }

  async deleteBot(botId: string) {
    if (
      await confirmDialog(lang.tr('admin.workspace.bots.confirmDelete'), {
        acceptLabel: lang.tr('admin.workspace.bots.item.yes')
      })
    ) {
      await api.getSecured().post(`/admin/workspace/bots/${botId}/delete`)
      this.props.fetchBots()
    }
  }

  async reloadBot(botId: string) {
    try {
      await api.getSecured().post(`/admin/workspace/bots/${botId}/reload`)
      this.props.fetchBots()
      this.props.fetchBotHealth()
      toast.success(lang.tr('admin.workspace.bots.remounted'))
    } catch (err) {
      console.error(err)
      toast.failure(lang.tr('admin.workspace.bots.couldNotMount'))
    }
  }

  viewLogs(botId: string) {
    this.props.history.push(
      generatePath('/workspace/:workspaceId?/logs?botId=:botId', {
        workspaceId: getActiveWorkspace() || undefined,
        botId
      })
    )
  }

  renderCreateNewBotButton() {
    return (
      <AccessControl resource="admin.bots.*" operation="write">
        <Popover minimal interactionKind={PopoverInteractionKind.HOVER} position={Position.BOTTOM}>
          <Button
            id="btn-create-bot"
            intent={Intent.NONE}
            text={lang.tr('admin.workspace.bots.createBot')}
            rightIcon="caret-down"
          />
          <ButtonGroup vertical={true} minimal={true} fill={true} alignText={Alignment.LEFT}>
            <Button
              id="btn-new-bot"
              text={lang.tr('admin.workspace.bots.new')}
              icon="add"
              onClick={() => this.setState({ isCreateBotModalOpen: true })}
            />
            <Button
              id="btn-import-bot"
              text={lang.tr('admin.workspace.bots.importExisting')}
              icon="import"
              onClick={() => this.setState({ isImportBotModalOpen: true })}
            />
          </ButtonGroup>
        </Popover>
      </AccessControl>
    )
  }

  hasUnlangedBots = () => {
    return this.props.bots.reduce((hasUnlangedBots, bot) => hasUnlangedBots || !bot.defaultLanguage, false)
  }

  async requestStageChange(botId: string) {
    await api.getSecured({ timeout: 60000 }).post(`/admin/workspace/bots/${botId}/stage`)
    this.props.fetchBots()
    toast.success(lang.tr('admin.workspace.bots.promoted'))
  }

  async approveStageChange(botId) {
    await api.getSecured({ timeout: 60000 }).post(`/admin/workspace/bots/${botId}/approve-stage`)
    this.props.fetchBots()
    toast.success(lang.tr('admin.workspace.bots.approvedPromotion'))
  }

  async createRevision(botId) {
    await api.getSecured().post(`admin/workspace/bots/${botId}/revisions`)
    toast.success(lang.tr('admin.workspace.bots.revisionsCreated'))
  }

  toggleRollbackModal = (botId?: string) => {
    this.setState({
      focusedBot: typeof botId === 'string' ? botId : null,
      isRollbackModalOpen: !this.state.isRollbackModalOpen
    })
  }

  toggleDeploymentModal = (botId?: string, domainList?: string[], details?: BotDetails, name?: string) => {
    this.setState({
      focusedBot: typeof botId === 'string' ? botId : null,
      whiteListedDomains: domainList ? domainList : [],
      details,
      name,
      isDeploymentModalOpen: !this.state.isDeploymentModalOpen
    })
  }

  handleRollbackSuccess = () => {
    this.props.fetchBots()
    toast.success(lang.tr('admin.workspace.bots.rollbackSuccess'))
  }

  handleEditStageSuccess = () => {
    this.props.fetchBots()
  }

  toggleEditStage = (stage?) => {
    this.setState({
      selectedStage: stage ? stage : null,
      isEditStageModalOpen: !this.state.isEditStageModalOpen
    })
  }

  toggleFilters = () => {
    this.setState({ showFilters: !this.state.showFilters })
  }

  findBotError(botId: string) {
    if (!this.props.health) {
      return false
    }

    return _.some(
      this.props.health.map(x => x.bots[botId]),
      s => s && s.status === 'unhealthy'
    )
  }

  renderCompactView(bots: BotConfig[]) {
    if (!bots.length) {
      return null
    }
    const { email } = this.props.profile || {}

    return (
      <div className={cx(style.bot_views, style.compact_view, style.table)}>
        {bots.map(bot => (
          <Fragment key={bot.id}>
            <BotItemCompact
              bot={bot}
              email={email}
              loadedModules={this.props.loadedModules}
              hasError={this.findBotError(bot.id)}
              deleteBot={this.deleteBot.bind(this, bot.id)}
              exportBot={this.exportBot.bind(this, bot.id)}
              createRevision={this.createRevision.bind(this, bot.id)}
              rollback={this.toggleRollbackModal.bind(this, bot.id)}
              deployment={this.toggleDeploymentModal.bind(this, bot.id, bot.whiteListedDomains, bot?.details, bot?.name)}
              reloadBot={this.reloadBot.bind(this, bot.id)}
              viewLogs={this.viewLogs.bind(this, bot.id)}
              installedNLULanguages={this.props.language}
              profile={this.props.profile}
            />
          </Fragment>
        ))}
      </div>
    )
  }

  renderPipelineView(bots: BotConfig[]) {
    const { pipeline } = this.props.workspace || {}
    const { email, strategy } = this.props.profile || {}

    const botsByStage = _.groupBy(bots, 'pipeline_status.current_stage.id')
    const colSize = Math.floor(12 / pipeline.length)

    return (
      <Fragment>
        <div className={cx(style.row, style.pipeline_view, style.bot_views)}>
          {pipeline.map((stage, idx) => {
            const allowStageChange = idx !== pipeline.length - 1
            return (
              <div key={stage.id} style={{ flex: colSize, marginRight: 20 }}>
                {pipeline.length > 1 && (
                  <div className={style.pipeline_title}>
                    <h3>{stage.label}</h3>
                    <AccessControl resource="admin.bots.*" operation="write" superAdmin>
                      <Button className={style.pipeline_edit_button} onClick={() => this.toggleEditStage(stage)}>
                        <Icon icon="edit" />
                      </Button>
                    </AccessControl>
                  </div>
                )}
                {idx === 0 && (
                  <div className={cx(style.pipeline_bot, style.create)}>{this.renderCreateNewBotButton()}</div>
                )}
                {(botsByStage[stage.id] || []).map(bot => (
                  <Fragment key={bot.id}>
                    <BotItemPipeline
                      loadedModules={this.props.loadedModules}
                      bot={bot}
                      isApprover={stage.reviewers.find(r => r.email === email && r.strategy === strategy) !== undefined}
                      userEmail={email!}
                      userStrategy={strategy!}
                      hasError={this.findBotError(bot.id)}
                      allowStageChange={allowStageChange && !bot.disabled}
                      requestStageChange={this.requestStageChange.bind(this, bot.id)}
                      approveStageChange={this.approveStageChange.bind(this, bot.id)}
                      deleteBot={this.deleteBot.bind(this, bot.id)}
                      exportBot={this.exportBot.bind(this, bot.id)}
                      createRevision={this.createRevision.bind(this, bot.id)}
                      rollback={this.toggleRollbackModal.bind(this, bot.id)}
                      deployment={this.toggleDeploymentModal.bind(this, bot.id, bot.whiteListedDomains, bot?.details, bot?.name)}
                      reloadBot={this.reloadBot.bind(this, bot.id)}
                      viewLogs={this.viewLogs.bind(this, bot.id)}
                      installedNLULanguages={this.props.language}
                    />
                  </Fragment>
                ))}
              </div>
            )
          })}
        </div>
      </Fragment>
    )
  }

  filterStageApproval(bot: BotConfig, email: string, strategy: string) {
    if (!this.props.workspace || !this.state.needApprovalFilter) {
      return true
    }
    const { pipeline } = this.props.workspace
    const { current_stage, stage_request } = bot.pipeline_status

    const reviewers = _.get(current_stage && pipeline.find(x => x.id === current_stage.id), 'reviewers', [])
    const isReviewer = reviewers.find(x => x.strategy === strategy && x.email === email)

    return stage_request && isReviewer
  }

  createNewBotUrl() {
    this.props.history.push(
      generatePath('/workspace/:workspaceId?/newBot', {
        workspaceId: getActiveWorkspace() || undefined
      })
    )
  }

  renderBots() {
    const { email, strategy } = this.props.profile || {}
    const branding = utils.getBranding()
    const brandName = branding?.pageTitle || 'EnableX Dialogs'

    const linkedBot = filterList<BotConfig>(this.props.bots, botFilterFields, this.state.filter).filter(x =>
      this.props.profile?.isAgentUser || this.props.profile?.isSupervisor ? this.props.botsByAgent?.includes(x.id) : true
    )
    const filteredBots = filterList<BotConfig>(this.props.bots, botFilterFields, this.state.filter).filter(
      x =>
        this.filterStageApproval(x, email!, strategy!) &&
        (this.props.profile?.isAgentUser || this.props.profile?.isSupervisor ? this.props.botsByAgent?.includes(x.id) : true)
    )
    const hasBots = !!linkedBot.length
    const botsView = this.isPipelineView ? this.renderPipelineView(filteredBots) : this.renderCompactView(filteredBots)
    // eslint-disable-next-line no-console
    return (
      <div className={cx(style.containerLeft, { [style.show]: !this.state.isLaptopScreen })}>
        <div className={cx({ [style.forLaptop]: !this.state.isLaptopScreen })}>
          <div className={style.filterWrapper}>
            <div className={style.pageTitle}>BOTs</div>
            <InputGroup
              id="input-filter"
              type="search"
              placeholder={lang.tr('admin.workspace.bots.filter')}
              value={this.state.filter}
              onChange={e => this.setState({ filter: e.target.value.toLowerCase() })}
              autoComplete="off"
              className={style.filterField}
              large={true}
            />
            {this.isPipelineView && <Button icon="filter" onClick={this.toggleFilters}></Button>}
            {this.props.profile?.isAdminUser ? (
              <ButtonGroup
                vertical={false}
                minimal={true}
                fill={false}
                alignText={Alignment.LEFT}
                className={style.buttonGroup}
              >
                <Button
                  id="btn-new-bot"
                  text={lang.tr('admin.workspace.bots.new')}
                  icon="add"
                  intent="primary"
                  className={style.newBotButton}
                  onClick={this.createNewBotUrl.bind(this)}
                />
                <Button
                  id="btn-import-bot"
                  text={lang.tr('admin.workspace.bots.importExisting')}
                  icon="import"
                  intent="none"
                  className={style.importBotButton}
                  onClick={() => this.setState({ isImportBotModalOpen: true })}
                />
              </ButtonGroup>
            ):
            <></>}
          </div>
          {!(this.props.profile?.isAgentUser || this.props.profile?.isSupervisor) ? (
            <div className={style.heroSectionText}>
              Manage your chatbots, monitor active platforms, and adjust configurations. Track linked agents, dive into
              analytics and engage in live chats. Export or deploy bots seamlessly as needed.
            </div>
          ) : (
            <div className={style.heroSectionText}>
              View all bots you're aligned with. Select any bot to dive into live chats and assist users in real-time.
            </div>
          )}

          {this.state.filter && !filteredBots.length && (
            <Callout title={lang.tr('admin.workspace.bots.noBotMatches')} className={style.filterCallout} />
          )}

          {!hasBots && !this.state.filter && (
            <Callout className={style.filterCallout}>
              <div className={style.noBotHero}>
                <div className={style.botLogo}>
                  <BotIcon />
                </div>
                <div className={style.heroTitle}>{lang.tr('admin.workspace.bots.noBotYet')}</div>
                <div className={style.noBotMsg}>
                  {this.props.profile?.isAdminUser ? (
                    <div>{lang.tr('admin.workspace.bots.alwaysAssignedToWorkspace')}</div>
                  ) : (this.props.profile?.isAgentUser || this.props.profile?.isSupervisor) ? (
                    <div>Welcome to the {brandName}!. Looks like you haven't aligned with any bot yet.</div>
                  ) : (
                    <div>Welcome to the {brandName}! Each bot belongs to a workspace.</div>
                  )}
                </div>

                {this.props.profile?.isAdminUser ? (
                  <div style={{ display: 'flex', gap: '15px', alignItems: 'center', margin: '1rem' }}>
                    <Button
                      className={style.createNewBotBtn}
                      large={true}
                      id="btn-new-bot"
                      text={lang.tr('admin.workspace.bots.new')}
                      icon="add"
                      intent="primary"
                      onClick={this.createNewBotUrl.bind(this)}
                    />

                    <Button
                      id="btn-import-bot"
                      text={lang.tr('admin.workspace.bots.importExisting')}
                      icon="import"
                      intent="none"
                      className={style.importBotButton}
                      onClick={() => this.setState({ isImportBotModalOpen: true })}
                    />
                  </div>
                ) : (
                  ''
                )}
              </div>
            </Callout>
          )}

          {this.hasUnlangedBots() && (
            <Callout intent={Intent.WARNING}>{lang.tr('admin.workspace.bots.noSpecifiedLanguage')}</Callout>
          )}
          {botsView}
        </div>
        {!this.state.isLaptopScreen && (
          <div
            style={{
              width: '25%',
              background: '#fff',
              height: 'fit-content',
              borderRadius: '10px',
              padding: '10px 1.5rem  1.5rem  1.5rem'
              // marginRight: '50px'
            }}
          >
            <h3>Get started with {brandName}</h3>
            <div>
              <video src={'https://portal.enablex.io/dialogs/EnableX_Engage_bot.mp4'} poster={'admin/video/video_poster.jpg'} preload="auto" style={{ width: '100%' }} controls />
            </div>
          </div>
        )}
      </div>
    )
  }

  get isPipelineView() {
    return this.props.workspace && this.props.workspace.pipeline && this.props.workspace.pipeline.length > 1
  }

  render() {
    if (!this.props.bots) {
      return <LoadingSection />
    }

    return (
      <PageContainer title={lang.tr('admin.workspace.bots.bots')} fullWidth={true}>
        {/* <SplitPage sideMenu={!this.isPipelineView && this.renderCreateNewBotButton()}> */}
        <Fragment>
          <Downloader url={this.state.archiveUrl} filename={this.state.archiveName} />
          {this.renderBots()}

          <AccessControl resource="admin.bots.*" operation="write">
            <RollbackBotModal
              botId={this.state.focusedBot}
              isOpen={this.state.isRollbackModalOpen}
              toggle={this.toggleRollbackModal}
              onRollbackSuccess={this.handleRollbackSuccess}
            />
            <DeployModal
              botId={this.state.focusedBot}
              whiteListedDomains={this.state.whiteListedDomains}
              details={this.state.details}
              name={this.state.name}
              isOpen={this.state.isDeploymentModalOpen}
              toggle={this.toggleDeploymentModal}
            />
            <EditStageModal
              workspace={this.props.workspace}
              stage={this.state.selectedStage}
              isOpen={this.state.isEditStageModalOpen}
              toggle={this.toggleEditStage}
              onEditSuccess={this.handleEditStageSuccess}
            />
            <ImportBotModal
              isOpen={this.state.isImportBotModalOpen}
              toggle={this.toggleImportBotModal}
              onCreateBotSuccess={this.props.fetchBots}
            />
          </AccessControl>
        </Fragment>
        {/* </SplitPage> */}
      </PageContainer>
    )
  }
}

const mapStateToProps = (state: AppState) => ({
  loadedModules: state.modules.loadedModules,
  bots: state.bots.bots,
  botsByAgent: state.bots.botsByAgent,
  health: state.bots.health,
  workspace: state.bots.workspace,
  loading: state.bots.loadingBots,
  profile: state.user.profile,
  language: state.bots.nluLanguages
})

const mapDispatchToProps = {
  fetchBots,
  fetchBotForAgent,
  fetchBotHealth,
  fetchModules,
  fetchBotNLULanguages
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(Bots)
